// Landing de l'Observatoire — décision C tranchée par Marc (12/07/2026).
// Angle maître (brief Marc) : LE GAIN. On agrège toutes les données officielles d'une
// commune à sa place = l'équivalent d'un poste à temps plein / data analyst territorial,
// pour le prix d'un abonnement. La fiabilité (officiel, à jour) est la PREUVE, pas le pitch.
// Une seule offre (100 % abonnement). Bouton n°1 « Voir ma commune » (compte gratuit = lead).
// Sobre, institutionnel, zéro look IA (CDC). Chiffres VÉRIFIÉS en prod (zéro invention).
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'
// Seules les icônes FONCTIONNELLES restent (Marc 14/09 : « les emoji font très IA ») :
// la loupe du bouton de recherche, les flèches d'action, les coches de liste. Toute
// icône purement décorative — pastille teintée d'une carte, pictogramme de section — a
// été retirée : elle n'ajoutait rien au sens et donnait à la page un air de gabarit IA.
import { Check, ArrowRight, Search } from 'lucide-react'
import SiteHeader from './components/SiteHeader'
import { NOM_PRODUIT } from './constants'
import { useStructure } from './structure'
import SiteFooter from './components/SiteFooter'
import { GAINS, OBTENEZ, SOURCES, STATS } from './landingContenu'
import AnchorLink, { scrollToAnchor } from './components/AnchorLink'
import { usePageMeta } from './hooks/usePageMeta'
// Cohérence visuelle avec l'observatoire /o (retour Marc 12/07) : mêmes polices que la
// maquette Cyrille — IBM Plex Serif (titres) + IBM Plex Sans (corps). La classe `.landing`
// (index.css) applique le serif aux titres ; le fond #F9FAFB reprend --c-bg de obs.css.
import '@fontsource/ibm-plex-serif/400.css'
import '@fontsource/ibm-plex-serif/600.css'
import '@fontsource/ibm-plex-sans/400.css'
import '@fontsource/ibm-plex-sans/500.css'
import '@fontsource/ibm-plex-sans/600.css'


// Recherche de commune directement depuis la landing (demande Marc 13/07) — même API que
// /o, styling Tailwind du hero. Sélection → droit sur la vue d'ensemble de la commune.
interface CommuneHit {
  code_commune: string; nom_commune: string; population: number; code_postal?: string
  // Commune DISPARUE : la recherche a répondu par la commune qui l'a absorbée. On le dit —
  // rediriger en silence laisserait croire à une faute de frappe (Marc, 30/07).
  redirection_fusion?: boolean; ancien_nom?: string; ancien_code?: string; fusion_date?: string
  // Nature du mouvement (#698) : elle choisit le libellé de la note. Un `recodage` du COG
  // n'a rien fusionné — la commune a changé de numéro, et parfois de nom.
  categorie?: 'fusion' | 'recodage' | 'renommage_fusion'
}
interface Palier { label: string; prix_annuel_ht: number }

interface EpciHit { siren: string; nom: string; nature_juridique?: string | null; nb_communes?: number | null }

function RechercheCommuneHero() {
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<CommuneHit[]>([])
  const [epciHits, setEpciHits] = useState<EpciHit[]>([])
  // Navigation clavier : communes puis intercommunalités.
  const [sel, setSel] = useState(-1)
  const debounce = useRef<number | null>(null)
  const generation = useRef(0)
  const navigate = useNavigate()
  const API = (import.meta.env.VITE_API_URL || '') + '/api'

  const total = hits.length + epciHits.length
  const allerA = (i: number) => {
    if (i < hits.length) navigate(`/o/${hits[i].code_commune}/synthese`)
    else navigate(`/epci/${epciHits[i - hits.length].siren}`)
  }
  const vider = () => { setHits([]); setEpciHits([]) }
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (total === 0) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => (s + 1) % total) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => (s - 1 + total) % total) }
    else if (e.key === 'Enter') { e.preventDefault(); allerA(sel >= 0 ? sel : 0) }
    else if (e.key === 'Escape') { vider(); setSel(-1) }
  }

  useEffect(() => {
    if (debounce.current) window.clearTimeout(debounce.current)
    setSel(-1)
    const terme = q.trim()
    if (terme.length < 2) { generation.current += 1; vider(); return }
    debounce.current = window.setTimeout(() => {
      // Deux requêtes partent à chaque frappe et reviennent dans le désordre : sans ce jeton,
      // la réponse lente de « Bor » pouvait écraser celle de « Bord », déjà affichée. Seule la
      // dernière génération a le droit d'écrire.
      const moi = ++generation.current
      const aJour = () => generation.current === moi
      axios.get<CommuneHit[]>(`${API}/communes/search`, { params: { q } })
        .then(r => { if (aJour()) setHits(r.data.slice(0, 5)) })
        .catch(() => { if (aJour()) setHits([]) })
      // P0-3 : la recherche accepte aussi les intercommunalités (page offre publique).
      axios.get<{ resultats: EpciHit[] }>(`${API}/observatoire/epci/search`, { params: { q } })
        .then(r => { if (aJour()) setEpciHits((r.data.resultats || []).slice(0, 3)) })
        .catch(() => { if (aJour()) setEpciHits([]) })
    }, 250)
    return () => { if (debounce.current) window.clearTimeout(debounce.current) }
  }, [q, API])

  return (
    // `data-anchor-top` : cliquer « L'Observatoire » ramène en haut de page et non sur ce
    // champ (Marc 02/08) — le hero doit être vu entier, titre et promesse compris.
    <div id="recherche" data-anchor-top className="relative max-w-xl mx-auto mt-8 text-left">
      <div className="flex items-center gap-3 bg-white border border-brand-200 rounded-xl px-4 py-3.5 shadow-sm focus-within:ring-2 focus-within:ring-accent-500">
        <Search size={18} className="text-brand-400 shrink-0" aria-hidden="true" />
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Commune (nom, code postal ou code INSEE), intercommunalité…"
          aria-label="Rechercher une commune par son nom, son code postal ou son code INSEE, une intercommunalité"
          aria-activedescendant={sel >= 0 ? `hero-hit-${sel}` : undefined}
          className="w-full outline-none text-brand-900 placeholder:text-brand-400 bg-transparent"
        />
      </div>
      {total > 0 && (
        <div className="absolute z-30 mt-2 w-full bg-white border border-brand-200 rounded-xl shadow-lg overflow-hidden" role="listbox">
          {hits.map((h, i) => (
            <button key={h.code_commune} id={`hero-hit-${i}`} role="option" aria-selected={sel === i}
              className={`w-full flex items-center justify-between px-4 py-3 text-sm transition text-left ${sel === i ? 'bg-brand-50' : 'hover:bg-brand-50'}`}
              onMouseEnter={() => setSel(i)}
              onClick={() => navigate(`/o/${h.code_commune}/synthese`)}>
              <span className="font-medium text-brand-900">
                {h.nom_commune}
                {/* Les DEUX codes, toujours. Taper « 63113 » rend Clermont-Ferrand (code
                    INSEE) ET Picherande (code postal) : sans les afficher, l'élu ne peut pas
                    savoir laquelle est la sienne — 2 708 collisions de ce type en France. */}
                <span className="ml-1 font-normal text-brand-400 text-xs">
                  INSEE {h.code_commune}{h.code_postal ? ` · CP ${h.code_postal}` : ''}
                </span>
                {h.redirection_fusion && (
                  <span className="block mt-0.5 text-xs font-normal text-amber-700">
                    {/* Tremblay-lès-Gonesse est devenue Tremblay-en-France au recodage de 1968
                        (#698) : ni « a fusionné » ni la date n'y seraient vrais. */}
                    {h.categorie === 'recodage'
                      ? `${h.ancien_nom} s'appelle aujourd'hui ${h.nom_commune}`
                      : <>
                          {h.ancien_nom} a fusionné
                          {h.fusion_date ? ` le ${new Date(h.fusion_date).toLocaleDateString('fr-FR')}` : ''}
                          {' — voir '}{h.nom_commune}
                        </>}
                  </span>
                )}
              </span>
              <span className="text-brand-500 shrink-0">
                {h.population ? `${h.population.toLocaleString('fr-FR')} hab` : ''}
              </span>
            </button>
          ))}
          {epciHits.length > 0 && (
            <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-brand-400 border-t border-brand-100">
              Intercommunalités — offre groupée
            </div>
          )}
          {epciHits.map((e, j) => {
            const i = hits.length + j
            return (
              <button key={e.siren} id={`hero-hit-${i}`} role="option" aria-selected={sel === i}
                className={`w-full flex items-center justify-between px-4 py-3 text-sm transition text-left ${sel === i ? 'bg-brand-50' : 'hover:bg-brand-50'}`}
                onMouseEnter={() => setSel(i)}
                onClick={() => navigate(`/epci/${e.siren}`)}>
                <span className="font-medium text-brand-900">{e.nom}</span>
                <span className="text-brand-500">
                  {e.nb_communes ? `${e.nb_communes} communes` : ''}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function LandingPage() {
  const API = (import.meta.env.VITE_API_URL || '') + '/api'
  const { hash } = useLocation()
  // A20 : nombre et liste des thématiques servis par le registre (repli : constants.ts).
  const structure = useStructure()
  const [paliers, setPaliers] = useState<Palier[]>([])
  // Le taux de TVA suit le régime fiscal servi par le serveur.
  const [tva, setTva] = useState(0)
  const [mentionTva, setMentionTva] = useState<string | null>(null)

  usePageMeta({
    title: `${NOM_PRODUIT} — Les données officielles de votre commune, réunies et expliquées`,
    description: "Les données officielles de votre commune, réunies, à jour et " +
      "analysées pour vous, pour le prix d'un " +
      "abonnement. Sources INSEE, DGFiP, France Travail. Devis et mandat administratif.",
  })

  useEffect(() => {
    axios.get<{ paliers: Palier[] }>(`${API}/observatoire/pricing`)
      .then(r => setPaliers(r.data.paliers)).catch(() => setPaliers([]))
    axios.get<{ tva: number; mention_tva: string | null }>(`${API}/pricing`)
      .then(r => { setTva(r.data.tva ?? 0); setMentionTva(r.data.mention_tva ?? null) })
      .catch(() => { setTva(0); setMentionTva(null) })
  }, [API])

  // Scroll à l'ARRIVÉE sur la page avec un hash (ex. /faq → /#tarifs). Le petit délai laisse
  // la section se monter. Les re-clics DEPUIS la page sont gérés par AnchorLink, qui ne
  // repasse pas par React Router — cet effet ne se redéclencherait pas (c'était le bug).
  useEffect(() => {
    if (!hash) return
    const t = window.setTimeout(() => scrollToAnchor(hash.slice(1)), 120)
    return () => window.clearTimeout(t)
  }, [hash])

  return (
    <div className="landing min-h-screen bg-[#F9FAFB] text-brand-900 flex flex-col">
      {/* a11y (Cato) : skip-link clavier/lecteur d'écran, comme le reste de l'app */}
      <a href="#contenu"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-3 focus:left-3 focus:bg-brand-900 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg">
        Aller au contenu
      </a>
      {/* ── Header ─────────────────────────────────────────────── */}
      <SiteHeader />

      <main id="contenu">
      {/* ── Hero — LE GAIN ─────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-14 text-center">
        <p className="text-xs font-semibold uppercase tracking-widest text-accent-600 mb-4">
          Observatoire territorial pour les collectivités
        </p>
        <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight max-w-3xl mx-auto">
          Les données officielles de votre commune,<br className="hidden sm:block" />
          <span className="text-accent-600"> réunies et tenues à jour pour vous.</span>
        </h1>
        <p className="mt-6 text-lg text-brand-600 max-w-2xl mx-auto leading-relaxed">
          Réunir et tenir à jour ces données demande du temps et des compétences rares.
          Vous accédez au résultat pour le prix d'un abonnement — INSEE, DGFiP,
          France Travail, ministères réunis en un seul endroit. Vous n'avez plus qu'à décider.
        </p>
        <RechercheCommuneHero />
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <AnchorLink to="/#recherche"
            className="inline-flex items-center justify-center gap-2 bg-brand-900 text-white font-semibold rounded-xl px-6 py-3.5 hover:bg-brand-800 transition">
            <Search size={18} /> Voir ma commune
          </AnchorLink>
          {/* « Demander une démo » pointait sur /collectivites, qui explique le circuit
              d'achat — pas de quoi demander une démo (Marc 02/08 : « ce n'est pas
              logique »). La demande de démo est un contact : elle va sur le formulaire. */}
          <Link to="/contact"
            className="inline-flex items-center justify-center gap-2 border border-brand-200 text-brand-800 font-semibold rounded-xl px-6 py-3.5 hover:bg-brand-50 transition">
            Demander une démo <ArrowRight size={18} />
          </Link>
        </div>
        <p className="mt-4 text-sm text-brand-600">
          Aperçu gratuit de votre commune ·
          paiement par mandat administratif (sans carte bancaire)
        </p>
      </section>

      {/* ── Bandeau chiffres massue ────────────────────────────── */}
      <section className="bg-brand-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {STATS(structure).map(s => (
            <div key={s.l}>
              <div className="serif text-3xl font-semibold">{s.v}</div>
              <div className="text-sm text-brand-200 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Ce que vous gagnez (le cœur ROI) ───────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-extrabold text-center mb-3">Ce que vous gagnez</h2>
        <p className="text-brand-600 text-center max-w-2xl mx-auto mb-10">
          {NOM_PRODUIT} ne vend pas de la donnée : il vous rend le temps, l'argent et la compétence
          qu'il faudrait pour la produire vous-même.
        </p>
        <div className="grid sm:grid-cols-2 gap-5">
          {GAINS(structure).map(({ titre, texte }) => (
            <div key={titre} className="border border-brand-100 rounded-xl p-6">
              <h3 className="font-bold text-lg mb-2">{titre}</h3>
              <p className="text-sm text-brand-600 leading-relaxed">{texte}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Ce que vous obtenez + capture réelle ───────────────── */}
      <section className="bg-brand-50 border-y border-brand-100">
        <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl font-extrabold mb-5">Ce que vous obtenez</h2>
            <ul className="space-y-3">
              {OBTENEZ(structure).map(t => (
                <li key={t} className="flex gap-3 text-brand-700">
                  <Check size={20} className="shrink-0 text-accent-600 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <AnchorLink to="/#recherche"
              className="mt-7 inline-flex items-center gap-2 text-accent-600 font-semibold hover:text-accent-700 transition">
              Voir ma commune <ArrowRight size={18} />
            </AnchorLink>
          </div>
          <div className="rounded-xl border border-brand-200 shadow-sm overflow-hidden bg-white">
            <div className="flex items-center gap-1.5 px-3 py-2 border-b border-brand-100 bg-brand-50">
              <span className="w-2.5 h-2.5 rounded-full bg-brand-200" />
              <span className="w-2.5 h-2.5 rounded-full bg-brand-200" />
              <span className="w-2.5 h-2.5 rounded-full bg-brand-200" />
              <span className="ml-2 text-xs text-brand-500">{NOM_PRODUIT} — Palaiseau</span>
            </div>
            {/* Capture RÉELLE de la vue publique de Palaiseau, prise en densité ×2 le
                02/08 (Marc : l'ancienne image de Saint-Denis piquait). Cadrée juste après
                les vrais chiffres : sur une section « Ce que vous obtenez », une rangée de
                cadenas dirait le contraire du message. Pas de maquette dessinée — tout
                l'argumentaire repose sur « ces chiffres sont réels », une illustration
                inviterait la question « c'est une vraie capture ? ». */}
            <img src="/apercu-observatoire.png"
              alt="Aperçu de l'observatoire de Palaiseau : ses thématiques et les premiers indicateurs"
              width={2400} height={870}
              className="w-full h-auto" loading="lazy" />
          </div>
        </div>
      </section>

      {/* ── Preuve : données officielles ───────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-16 text-center">
        <h2 className="text-3xl font-extrabold mb-3">Des données officielles, sourcées et datées</h2>
        <p className="text-brand-600 max-w-2xl mx-auto mb-8">
          Chaque indicateur affiche <strong>sa source et sa date</strong>. Les données sont
          actualisées tous les mois.
        </p>
        <div className="flex flex-wrap justify-center gap-2.5">
          {SOURCES.map(s => (
            <span key={s} className="text-sm font-medium text-brand-700 bg-brand-50 border border-brand-100 rounded-full px-4 py-1.5">
              {s}
            </span>
          ))}
        </div>
      </section>

      {/* ── Tarifs (aperçu) ────────────────────────────────────── */}
      <section id="tarifs" className="scroll-mt-24 bg-brand-50 border-y border-brand-100">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-xs font-semibold uppercase tracking-widest text-accent-600 mb-4">
              Tarifs
            </p>
            <h2 className="text-3xl font-extrabold mb-3">Un tarif juste, indexé sur la population</h2>
            <p className="text-brand-600 mb-10">
              Abonnement annuel · les {structure.nb_themes} thématiques incluses (tout ou rien, sans module caché).
            </p>
          </div>

          {paliers.length > 0 && (
            // flex-wrap centré (pas grid) : 7 paliers → la dernière ligne incomplète reste
            // centrée à chaque breakpoint ; largeurs calc = équivalent grille 2/3/4 col (gap-4 = 1rem).
            <div className="flex flex-wrap justify-center gap-4">
              {paliers.map((p) => (
                <article key={p.label}
                  className="w-full sm:w-[calc((100%_-_1rem)/2)] lg:w-[calc((100%_-_2rem)/3)] xl:w-[calc((100%_-_3rem)/4)] rounded-2xl border border-brand-200 bg-white p-6 shadow-sm text-center">
                  <p className="text-sm font-medium text-brand-500 mb-4">{p.label}</p>
                  <p className="text-3xl font-semibold serif text-brand-900">
                    {p.prix_annuel_ht.toLocaleString('fr-FR')} € HT
                  </p>
                  {/* Le TTC suit le régime fiscal servi par le serveur, indépendamment du SIRET.
                      La mention de franchise éventuelle figure une seule fois sous la grille. */}
                  {tva > 0 && (
                    <p className="mt-2 text-sm text-brand-600">
                      {Math.round(p.prix_annuel_ht * (1 + tva)).toLocaleString('fr-FR')} € TTC /an
                    </p>
                  )}
                </article>
              ))}
            </div>
          )}

          {/* La mention de TVA, UNE fois, sous la grille — pas sur chacune des 7 cartes.
              Elle honore les prix, elle ne doit pas les noyer. Disparaît au flip SIRET,
              où c'est la ligne TTC de chaque carte qui prend le relais. */}
          {paliers.length > 0 && tva === 0 && mentionTva && (
            <p className="mt-6 text-center text-sm text-brand-500">{mentionTva}</p>
          )}

          <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
            <Link to="/epci"
              className="group bg-white rounded-2xl border border-brand-200 p-6 text-left hover:border-accent-400 transition">
              <p className="text-xs font-semibold uppercase tracking-wide text-accent-600">
                Intercommunalités
              </p>
              <p className="font-bold text-brand-900 mt-2">Équipez toutes vos communes membres</p>
              <p className="text-sm text-brand-600 mt-1.5 leading-relaxed">
                Somme des abonnements communaux, décote intercommunale appliquée. Cherchez
                votre EPCI : le prix s'affiche.
              </p>
              <span className="text-sm text-accent-600 font-medium mt-3 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Calculer mon prix <ArrowRight size={15} />
              </span>
            </Link>
            <Link to="/contact" className="group bg-white rounded-2xl border border-brand-200 p-6 text-left hover:border-accent-400 transition">
              <p className="font-bold text-brand-900">Pour toute autre échelle territoriale, contactez-nous</p>
            </Link>
          </div>

          <p className="mt-6 text-center text-sm text-brand-600">
            Un bouquet de communes hors EPCI ou une métropole ?{' '}
            <Link to="/contact" className="font-medium text-accent-600 hover:text-accent-700 transition">Contactez-nous</Link>.
          </p>
        </div>
      </section>

      {/* ── Comment acheter (B2G) ──────────────────────────────── */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="border border-brand-200 rounded-xl p-8 md:p-10 bg-white">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <h2 className="text-2xl font-extrabold mb-2">Conçu pour l'achat public</h2>
              <p className="text-brand-600">
                Devis, bon de commande, facture sur <strong>Chorus Pro</strong>, règlement par
                <strong> mandat administratif</strong> : votre circuit habituel, sans carte
                bancaire. L'accès est activé à réception de votre bon de commande.
              </p>
            </div>
            <Link to="/collectivites"
              className="shrink-0 inline-flex items-center gap-2 bg-brand-900 text-white font-semibold rounded-xl px-5 py-3 hover:bg-brand-800 transition">
              Comment acheter <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Équipe sobre ───────────────────────────────────────── */}
      <section className="bg-brand-50 border-y border-brand-100">
        <div className="max-w-4xl mx-auto px-6 py-14 text-center">
          <h2 className="text-2xl font-extrabold mb-2">Qui est derrière {NOM_PRODUIT}</h2>
          <p className="text-brand-600 max-w-xl mx-auto mb-8">
            Une équipe française, joignable — pas une boîte noire. On répond, on corrige, et
            on rend des comptes sur chaque chiffre.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {/* Gouvernance réelle (Marc 02/08) : les deux sont co-fondateurs, Cyrille
                préside, Marc dirige. Même ordre et mêmes titres que les mentions légales —
                les deux pages doivent dire la même chose. */}
            <div className="border border-brand-100 rounded-xl px-8 py-6 bg-white">
              <div className="font-bold">Cyrille</div>
              <div className="text-sm text-brand-600">cofondateur et président</div>
            </div>
            <div className="border border-brand-100 rounded-xl px-8 py-6 bg-white">
              <div className="font-bold">Marc</div>
              <div className="text-sm text-brand-600">cofondateur et directeur général</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA final ──────────────────────────────────────────── */}
      <section className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h2 className="text-3xl font-extrabold mb-4">Voyez votre commune maintenant</h2>
        <p className="text-brand-600 mb-8">
          Accédez à un aperçu des chiffres publics de votre commune en une minute.
          Vous déciderez ensuite.
        </p>
        <AnchorLink to="/#recherche"
          className="inline-flex items-center justify-center gap-2 bg-accent-600 text-white font-semibold rounded-xl px-8 py-4 text-lg hover:bg-accent-700 transition">
          <Search size={20} /> Voir ma commune
        </AnchorLink>
      </section>
      </main>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <SiteFooter />
    </div>
  )
}
